import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  CheckCircle2,
  Crown,
  FileText,
  Inbox,
  Mail,
  ShieldCheck,
  Stamp,
  TimerReset,
  UserPlus,
  Users,
} from 'lucide-react';

import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ErrorState } from '@/components/ui/ErrorState';
import { getAdminMetricsOverview, type MetricsPeriodo } from '../../api/adminMetrics';
import { classifyError } from '../../lib/errors';
import { MetricsSection } from './dashboard/MetricsSection';

const PERIODOS: { value: MetricsPeriodo; label: string }[] = [
  { value: '7d', label: '7 dias' },
  { value: '30d', label: '30 dias' },
  { value: '90d', label: '90 dias' },
  { value: 'total', label: 'Total' },
];

const pct = (v: number) => `${v}%`;

function AdminDashboardPage() {
  const [periodo, setPeriodo] = useState<MetricsPeriodo>('30d');

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['admin', 'metrics', 'overview', periodo],
    queryFn: () => getAdminMetricsOverview(periodo),
  });

  const ativacao = data?.ativacao;
  const assinaturas = data?.assinaturas;
  const uso = data?.uso;

  const periodoLabel = periodo === 'total' ? 'no total' : `nos últimos ${PERIODOS.find((p) => p.value === periodo)?.label}`;
  const novosLabel = periodo === 'total' ? 'Total' : PERIODOS.find((p) => p.value === periodo)?.label;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-[var(--color-text-primary)]">Painel Administrativo</h1>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">Indicadores de uso e adoção da plataforma.</p>
        </div>
        <Tabs value={periodo} onValueChange={(v) => setPeriodo(v as MetricsPeriodo)}>
          <TabsList>
            {PERIODOS.map((p) => (
              <TabsTrigger key={p.value} value={p.value}>
                {p.label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      </div>

      {isError && !isLoading ? (
        <ErrorState
          title="Não foi possível carregar os indicadores"
          description={classifyError(error).message}
        />
      ) : (
        <div className="space-y-6">
          <MetricsSection
            title="Ativação"
            loading={isLoading}
            cards={[
              {
                title: 'Usuários ativos',
                value: ativacao?.usuarios_ativos ?? 0,
                icon: <Users />,
                subtitle: ativacao ? `${ativacao.usuarios_total} no total` : undefined,
              },
              {
                title: 'Onboarding completo',
                value: ativacao ? pct(ativacao.onboarding_pct) : 0,
                icon: <CheckCircle2 />,
                subtitle: ativacao ? `${ativacao.onboarding_completo} usuários` : undefined,
              },
              {
                title: 'Aptos a peticionar',
                value: ativacao?.aptos_peticionar ?? 0,
                icon: <ShieldCheck />,
                subtitle: ativacao ? `de ${ativacao.aptos_verificados} verificados` : undefined,
              },
              {
                title: `Novos (${novosLabel})`,
                value: ativacao?.novos_periodo ?? 0,
                icon: <UserPlus />,
              },
            ]}
          />

          <MetricsSection
            title="Assinaturas"
            loading={isLoading}
            cards={[
              { title: 'Trial ativo', value: assinaturas?.trial_ativo ?? 0, icon: <TimerReset /> },
              { title: 'Trial expirado', value: assinaturas?.trial_expirado ?? 0, icon: <TimerReset /> },
              { title: 'Cortesia ativa', value: assinaturas?.cortesia_ativa ?? 0, icon: <Crown /> },
              { title: 'Expirando em 7 dias', value: assinaturas?.expirando_7d ?? 0, icon: <Bell /> },
            ]}
          />

          <MetricsSection
            title={`Uso ${periodoLabel}`}
            loading={isLoading}
            cards={[
              { title: 'Comunicações ingestadas', value: uso?.comunicacoes_ingestadas ?? 0, icon: <Inbox /> },
              {
                title: 'Comunicações lidas',
                value: uso ? pct(uso.comunicacoes_lidas_pct) : 0,
                icon: <Mail />,
                subtitle: 'taxa total',
              },
              { title: 'Petições criadas', value: uso?.peticoes_criadas ?? 0, icon: <FileText /> },
              {
                title: 'Protocolos',
                value: uso?.protocolos_realizados ?? 0,
                icon: <Stamp />,
                subtitle: uso ? `${uso.protocolo_sucesso_pct}% sucesso` : undefined,
              },
            ]}
          />
        </div>
      )}
    </div>
  );
}

export default AdminDashboardPage;
